import { useEffect, useState, useCallback } from 'react'
import { getLocalApiUrl, authFetch } from '../config/api'

/**
 * Aggregate tile data for the governance + board dashboards (I12).
 *
 * Fans out to the four P1-item stats endpoints in parallel. Each
 * endpoint has a graceful fallback on the server (empty snapshot when
 * its table/columns aren't migrated yet), so an unavailable source
 * returns zeros rather than breaking the whole dashboard.
 *
 *   - cddTier         GET /api/cdd-tier/stats
 *   - riskRegister    GET /api/risk-items/stats
 *   - ttrRegistry     GET /api/ttr-registry/stats
 *   - rules           GET /api/rules/stats      (rationale coverage)
 *
 * Shape returned to the caller is normalised and safe to read even
 * before the first fetch completes.
 */

const EMPTY = Object.freeze({
  cddTier: { by_tier: {}, total_locked: 0 },
  riskRegister: { by_status: {}, overdue_reviews: 0 },
  ttrRegistry: { by_status: {}, overdue_drafts: 0, filed_last_7_days: 0 },
  rules: {
    total: 0,
    with_author_rationale: 0,
    with_backfill_rationale: 0,
    missing_rationale: 0,
    published_needing_review: 0,
  },
})

async function safeFetch(path) {
  try {
    const res = await authFetch(getLocalApiUrl(path))
    if (!res.ok) return null
    const json = await res.json().catch(() => null)
    if (!json || !json.success) return null
    return json.data
  } catch (_) {
    return null
  }
}

export function useGovernanceStats({ enabled = true } = {}) {
  const [data, setData] = useState(EMPTY)
  const [loading, setLoading] = useState(Boolean(enabled))
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [cdd, risk, ttr, rules] = await Promise.all([
        safeFetch('/api/cdd-tier/stats'),
        safeFetch('/api/risk-items/stats'),
        safeFetch('/api/ttr-registry/stats'),
        safeFetch('/api/rules/stats'),
      ])
      setData({
        cddTier: cdd || EMPTY.cddTier,
        riskRegister: risk || EMPTY.riskRegister,
        ttrRegistry: ttr || EMPTY.ttrRegistry,
        rules: rules || EMPTY.rules,
      })
    } catch (e) {
      setError(e?.message || 'Failed to load governance stats')
      setData(EMPTY)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!enabled) return
    load()
  }, [load, enabled])

  return { data, loading, error, reload: load }
}

/**
 * Derived helpers — kept here so every consumer computes the same way.
 */
export function cddTierCount(stats, tier) {
  return Number(stats?.cddTier?.by_tier?.[tier]?.count || 0)
}

export function riskOpenCount(stats) {
  const s = stats?.riskRegister?.by_status || {}
  return Number(s.open || 0) + Number(s.mitigating || 0)
}

export function ttrOpenCount(stats) {
  const s = stats?.ttrRegistry?.by_status || {}
  return Number(s.draft || 0) + Number(s.ready_for_submission || 0)
}
